import { useEffect, useRef, useState } from 'react';
import { useActiveContextOptional } from '@/auth/ActiveContextProvider';
import { useAccessEpoch } from '@/lib/access-epoch';
import { readPermissions } from './api';

/**
 * What the caller may do in «Comunicazioni» in the active organization
 * (`GET /v1/access/my-permissions?module=communications`). ADVISORY, and it FAILS CLOSED: loading
 * and error hold no permission. Same shape and rules as `useToolsPermissions`.
 */
export type CommunicationsPermissionsState =
  { status: 'loading' } | { status: 'ready'; permissions: readonly string[] } | { status: 'error' };

export function useCommunicationsPermissions(): CommunicationsPermissionsState {
  const [state, setState] = useState<CommunicationsPermissionsState>({ status: 'loading' });
  const [answeredFor, setAnsweredFor] = useState<string | undefined>(undefined);
  const context = useActiveContextOptional();
  const contextStatus = context?.status;
  const organizationId = context?.active?.organizationId;
  const tenantId = context?.active?.tenantId;
  const epoch = useAccessEpoch();
  const answeredOrg = useRef<string | undefined>(undefined);

  useEffect(() => {
    const refresh = answeredOrg.current !== undefined && answeredOrg.current === organizationId;
    setAnsweredFor(organizationId);
    if (contextStatus === 'loading') {
      setState({ status: 'loading' });
      return;
    }
    if (tenantId === undefined || organizationId === undefined) {
      setState({ status: 'ready', permissions: [] });
      return;
    }
    let live = true;
    if (!refresh) {
      answeredOrg.current = undefined;
      setState({ status: 'loading' });
    }
    void readPermissions(tenantId, organizationId).then((answer) => {
      if (!live) return;
      if (!answer.ok) {
        if (!refresh) setState({ status: 'error' });
        return;
      }
      answeredOrg.current = organizationId;
      setState({ status: 'ready', permissions: answer.value.permissions ?? [] });
    });
    return () => {
      live = false;
    };
  }, [contextStatus, tenantId, organizationId, epoch]);

  return answeredFor === organizationId ? state : { status: 'loading' };
}
